import { create } from '@bufbuild/protobuf';

import * as pbm from '@/grpc-gen/message_rule_pb';
import * as pbs from '@/grpc-gen/message_center_service_pb';

import { cloneModel } from '@/model/model-utils';
import { EmailMessageTypeChannel } from '@/model/MessageType';
import { BuiltInEventTypes } from '@/model/MessageEventType';
import MessageTemplate from './message-template';
import Tab from './tab';
import Campaign from './campaign';
import Service from './reservation-service';
import EventType from './event-type';
import MessageType from './message-type';
import Handlebar from './handlebar';

export interface MessageRuleMapping {
  messageCenterEventTypesById: Map<number, EventType>;
  messageCenterCampaignsById: Map <number, Campaign>;
  messageCenterHandlebarsById: Map<number, Handlebar>,
  messageCenterMessageTypesById: Map<number, MessageType>,
  messageCenterServicesById: Map<number, Service>,
  messageCenterTabsById: Map<number, Tab>,
}

export default class MessageRule {
  id: number = 0;

  sendBcc: boolean = false;

  isInactive: boolean = false;

  isDeleted: boolean = false;

  sendAtSpecificTime?: string;

  sendDaysInAdvance?: number;

  sendDaysAfter?: number;

  templates: MessageTemplate[] = [];

  obsoleteTemplates: MessageTemplate[] = [];

  campaign?: Campaign;

  service?: Service;

  tab?: Tab;

  eventType?: EventType;

  messageType?: MessageType;

  isDisabled?: boolean; // cannot be edited, displayed as inactive

  get isDefault() {
    // an UNSAVED rule (id 0) is never the default rule: while creating, picking Email +
    // a built-in event type must not lock the event selector or hide advanced filtering
    // (the unset service/campaign/section are exactly what makes this true, so the user
    // could never reach the controls that make the rule non-default). Saved rules (id > 0)
    // and the virtual built-in placeholders (negative id) classify as before.
    return this.id !== 0
    && this.messageType?.messageType === EmailMessageTypeChannel
    && BuiltInEventTypes.includes(this.eventType?.type ?? '')
    && this.service === undefined && this.campaign === undefined && this.tab === undefined;
  }

  static fromGrpcModel(o?: pbm.MessageRule, m?: MessageRuleMapping): MessageRule {
    const messageRule = new MessageRule();

    messageRule.id = Number(o?.id ?? 0);
    messageRule.sendBcc = o?.sendBcc ?? false;
    messageRule.isInactive = o?.isInactive ?? false;
    messageRule.isDeleted = o?.isDeleted ?? false;
    // keep 0: it is a real value ("Same day") - `|| undefined` collapsed it and the
    // schedule selects showed empty for a configured same-day rule
    messageRule.sendDaysAfter = o?.sendDaysAfter ?? undefined;
    messageRule.sendDaysInAdvance = o?.sendDaysInAdvance ?? undefined;
    messageRule.sendAtSpecificTime = o?.sendAtSpecificTime ?? '12:00';

    // map related entities using the mapping
    messageRule.campaign = m?.messageCenterCampaignsById.get(Number(o?.campaignId ?? 0));
    messageRule.service = m?.messageCenterServicesById.get(Number(o?.serviceId ?? 0));
    messageRule.eventType = m?.messageCenterEventTypesById.get(Number(o?.eventTypeId ?? 0));
    messageRule.messageType = m?.messageCenterMessageTypesById.get(Number(o?.messageTypeId ?? 0));
    messageRule.tab = m?.messageCenterTabsById.get(Number(o?.tabId ?? 0));

    // map templates
    messageRule.templates = o?.templates?.map((t) => MessageTemplate.fromGrpcModel(t)) ?? [];
    messageRule.obsoleteTemplates = o?.obsoleteTemplates?.map((t) => MessageTemplate.fromGrpcModel(t, { isObsolete: true })) ?? [];

    return messageRule;
  }

  static fromGrpcResponse(o?: pbs.MessageRuleResponse, m?: MessageRuleMapping): MessageRule {
    return MessageRule.fromGrpcModel(o?.rule, m);
  }

  toGrpcModel(): pbm.MessageRule {
    return create(pbm.MessageRuleSchema, {
      id: BigInt(this.id),
      sendBcc: this.sendBcc ?? false,
      isInactive: this.isInactive ?? false,
      isDeleted: this.isDeleted ?? false,
      sendDaysAfter: this.sendDaysAfter ?? 0,
      sendDaysInAdvance: this.sendDaysInAdvance ?? 0,
      sendAtSpecificTime: this.sendAtSpecificTime ?? '12:00',
      messageTypeId: BigInt(this.messageType?.id ?? 0),
      eventTypeId: BigInt(this.eventType?.id ?? 0),
      campaignId: BigInt(this.campaign?.id ?? 0),
      serviceId: BigInt(this.service?.id ?? 0),
      tabId: BigInt(this.tab?.id ?? 0),
      templates: this.isDeleted ? [] : this.templates.flatMap((t) => (t.isDeleted || !t.isExactMatch ? [] : [t.toGrpcModel()])),
      obsoleteTemplates: this.isDeleted ? [] : this.obsoleteTemplates.flatMap((t) => (t.isDeleted ? [] : [t.toGrpcModel()])),
      accountId: 0n,
    });
  }

  clone(): MessageRule {
    const copy = new MessageRule();
    Object.assign(copy, this);
    copy.templates = this.templates.map((o) => cloneModel(o));
    copy.obsoleteTemplates = this.obsoleteTemplates.map((o) => cloneModel(o));
    return copy;
  }

  static compare(o1: MessageRule, o2: MessageRule): number {
    let res = (o1.messageType?.id ?? 0) - (o2.messageType?.id ?? 0); if (res !== 0) return res;
    res = (o1.eventType?.id ?? 0) - (o2.eventType?.id ?? 0); if (res !== 0) return res;
    res = (o1.campaign?.id ?? 0) - (o2.campaign?.id ?? 0); if (res !== 0) return res;
    res = (o1.service?.id ?? 0) - (o2.service?.id ?? 0); if (res !== 0) return res;
    res = (o1.tab?.id ?? 0) - (o2.tab?.id ?? 0); if (res !== 0) return res;
    return 0;
  }
}
